import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, MapPin, AlertCircle } from 'lucide-react'

/**
 * Accessible portal footer. Sections are <nav> landmarks.
 * Content is real where verified; unverified external links are omitted.
 */
export function Footer() {
  const { t } = useTranslation()
  const year = new Date().getFullYear()

  const cols: { title: string; links: { label: string; path: string }[] }[] = [
    {
      title: t('footer.getHelp', 'Get Help'),
      links: [
        { label: t('nav.iHaveTb', 'I Have TB'), path: '/i-have-tb' },
        { label: t('nav.getHelp', 'Get Help'), path: '/get-help' },
        { label: t('nav.contact', 'Contact'), path: '/contact' },
        { label: t('nav.supportAPatient', 'Support a Patient'), path: '/support-a-patient' },
      ],
    },
    {
      title: t('footer.learn', 'Learn & Participate'),
      links: [
        { label: t('nav.aboutTb', 'About TB'), path: '/about-tb' },
        { label: t('nav.resources', 'Resources'), path: '/resources' },
        { label: t('nav.stories', 'Stories'), path: '/stories' },
        { label: t('nav.campaigns', 'Campaigns'), path: '/campaigns' },
      ],
    },
    {
      title: t('footer.getInvolved', 'Get Involved'),
      links: [
        { label: t('footer.becomeMitra', 'Become a TB Supporter (Ni-kshay Mitra)'), path: '/support-a-patient#nikshay-mitra' },
        { label: t('footer.volunteer', 'Volunteer / NGO'), path: '/get-help' },
        { label: t('footer.community', 'Community'), path: '/feed' },
      ],
    },
  ]

  return (
    <footer className="bg-warm-900 text-snow-100 mt-16">
      {/* Helpline banner */}
      <div className="bg-warm-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-human-300" aria-hidden="true" />
            <span>
              {t('footer.emergency', 'If you have TB symptoms (persistent cough, fever, weight loss), seek care at your nearest government health facility.')}
            </span>
          </div>
          <a href="tel:1800116666" className="flex items-center gap-1.5 font-semibold text-white hover:text-human-200 shrink-0">
            <Phone className="w-4 h-4" aria-hidden="true" />
            1800-11-6666
          </a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand col */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[rgb(75_200_255)] to-[rgb(0_180_216)] flex items-center justify-center text-white font-extrabold" aria-hidden="true">
                त
              </span>
              <span className="font-extrabold text-white">TB Mukt Bharat</span>
            </div>
            <p className="text-sm text-snow-300 mb-4">
              {t('footer.mission', 'A national public-health platform to help every visitor find TB information, support and services.')}
            </p>
            <ul className="space-y-2 text-sm text-snow-300">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 shrink-0 text-human-300" aria-hidden="true" />
                <a href="tel:1800116666" className="hover:text-white">1800-11-6666 (helpline)</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 shrink-0 text-human-300" aria-hidden="true" />
                <a href="/contact" className="hover:text-white">Contact us</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-human-300" aria-hidden="true" />
                <span>{t('footer.india', 'India')}</span>
              </li>
            </ul>
          </div>

          {/* Link columns */}
          {cols.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">{col.title}</h2>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.path}>
                    <Link to={l.path} className="text-sm text-snow-300 hover:text-white hover:underline">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-snow-400">
          <div className="flex flex-col items-center sm:items-start gap-1">
            <p>© {year} TB Mukt Bharat. {t('footer.rights', 'All rights reserved.')}</p>
            <p className="text-snow-400">· {t('footer.initiative', 'An initiative for a TB-Mukt India · National TB Elimination Programme (NTEP)')} ·</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/about-tb" className="hover:text-white">{t('footer.about', 'About')}</Link>
            <Link to="/contact" className="hover:text-white">{t('footer.contact', 'Contact')}</Link>
            <Link to="/login" className="hover:text-white">{t('nav.login', 'Login')}</Link>
          </div>
        </div>
        <p className="text-[11px] text-snow-400 mt-4 max-w-3xl">
          {t('footer.disclaimer', 'Health information on this site is for public awareness and does not replace professional medical advice. For diagnosis and treatment, consult a qualified health worker at your nearest facility. Some content is awaiting verification and is marked accordingly.')}
        </p>
      </div>
    </footer>
  )
}